import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/components/shared-components/confirmation-dialog/confirmation-dialog.component';
import { SnackBarService } from 'src/app/components/shared-components/snack-bar/snack-bar.service';
import { LOGIN_VERIFICATION_CONTACT_TYPES } from 'src/app/models/login-verification-model/login-verification-contact-type.model';
import { LoginVerificationContact } from 'src/app/models/login-verification-model/login-verification-contact.model';
import { UserService } from 'src/app/services/user-service/user.service';

@Component({
  selector: 'app-users-user-login-verification-contacts-card',
  templateUrl: './users-user-login-verification-contacts-card.component.html',
  styleUrls: ['./users-user-login-verification-contacts-card.component.scss']
})
export class UsersUserLoginVerificationContactsCardComponent implements OnInit, OnDestroy {

  @Input() user;

  private static readonly INTERNATIONAL_PHONE_REGEX = /^\+[1-9][0-9]{7,14}$/;

  public contactTypes = LOGIN_VERIFICATION_CONTACT_TYPES;
  public showButton = true;
  public contactForm = new FormGroup({
    type: new FormControl(null, Validators.required),
    value: new FormControl(null, Validators.required)
  });
  private addContactSubscription: Subscription;
  private deleteContactSubscription: Subscription;
  private dialogSubscription: Subscription;
  private typeChangeSubscription: Subscription;

  constructor(private userService: UserService,
              private snackbarService: SnackBarService,
              private dialog: MatDialog) { }

  ngOnInit() {
    this.typeChangeSubscription = this.contactForm.get('type').valueChanges.subscribe(
      (type) => this.updateValueValidators(type)
    );
    this.updateValueValidators(this.contactForm.get('type').value);
  }

  public addContact() {
    if (!this.user || !this.showButton) {
      return;
    }

    const validation = this.validateForm();
    if (validation !== true) {
      this.snackbarService.open(validation, 'error');
      return;
    }

    this.showButton = false;
    this.addContactSubscription = this.userService.addLoginVerificationContact(
      this.user.InternalId,
      this.contactForm.get('type').value,
      this.getNormalizedContactValue()
    ).subscribe(
      (res) => {
        this.showButton = true;
        this.contactForm.reset();
        this.snackbarService.open('users-page.snackbar messages.add login verification contact success', 'success');
        this.userService.refreshUser();
      },
      (error) => {
        this.showButton = true;
        this.snackbarService.open(this.resolveErrorMessage(error), 'error');
      }
    );
  }

  public deleteContact(contact: LoginVerificationContact) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'important',
        messages: ['users-page.delete login verification contact warning']
      }
    });

    this.dialogSubscription = dialogRef.afterClosed().subscribe(
      (accepted) => {
        if (accepted) {
          this.deleteContactSubscription = this.userService.deleteLoginVerificationContact(contact.InternalId).subscribe(
            (res) => {
              this.snackbarService.open('users-page.snackbar messages.delete login verification contact success', 'success');
              this.userService.refreshUser();
            },
            (error) => {
              this.snackbarService.open(this.resolveErrorMessage(error), 'error');
            }
          );
        }
      }
    );
  }

  public validateForm() {
    const type = this.contactForm.get('type').value;
    const value = this.getTrimmedContactValue();

    if (!this.contactForm.get('type').valid) {
      return 'users-page.snackbar messages.login verification contact type required';
    }
    if (!value) {
      return 'users-page.snackbar messages.login verification contact value required';
    }
    if (type === 'EMAIL' && !this.contactForm.get('value').valid) {
      return 'users-page.snackbar messages.login verification contact email invalid';
    }
    if (type === 'SMS' && !this.isValidInternationalPhone(value)) {
      return 'users-page.snackbar messages.login verification contact phone invalid';
    }
    return true;
  }

  public getValuePlaceholder() {
    const type = this.contactForm.get('type').value;
    if (type === 'EMAIL') {
      return 'users-page.enter login verification contact email value';
    }
    if (type === 'SMS') {
      return 'users-page.enter login verification contact sms value';
    }
    return 'users-page.enter login verification contact value';
  }

  public getValueInputType() {
    return this.contactForm.get('type').value === 'SMS' ? 'tel' : 'email';
  }

  public normalizeContactValue() {
    if (this.contactForm.get('type').value === 'SMS') {
      this.contactForm.get('value').setValue(this.normalizeInternationalPhone(this.contactForm.get('value').value));
    }
  }

  private updateValueValidators(type) {
    const validators = [Validators.required];
    if (type === 'EMAIL') {
      validators.push(Validators.email);
    }
    if (type === 'SMS') {
      validators.push((control) => this.isValidInternationalPhone(control.value) ? null : { invalidPhone: true });
    }
    this.contactForm.get('value').setValidators(validators);
    this.contactForm.get('value').updateValueAndValidity();
  }

  private getNormalizedContactValue() {
    const value = this.getTrimmedContactValue();
    if (this.contactForm.get('type').value === 'SMS') {
      return this.normalizeInternationalPhone(value);
    }
    if (this.contactForm.get('type').value === 'EMAIL') {
      return value.toLowerCase();
    }
    return value;
  }

  private getTrimmedContactValue() {
    return (this.contactForm.get('value').value || '').toString().trim();
  }

  private normalizeInternationalPhone(value) {
    return (value || '').toString().replace(/[()\s.-]/g, '');
  }

  private isValidInternationalPhone(value) {
    return UsersUserLoginVerificationContactsCardComponent.INTERNATIONAL_PHONE_REGEX.test(this.normalizeInternationalPhone(value));
  }

  private resolveErrorMessage(error) {
    if (error && error['error'] && error['error']['message']) {
      return error['error']['message'];
    }
    return 'users-page.snackbar messages.login verification contact error';
  }

  ngOnDestroy() {
    if (this.addContactSubscription) { this.addContactSubscription.unsubscribe(); }
    if (this.deleteContactSubscription) { this.deleteContactSubscription.unsubscribe(); }
    if (this.dialogSubscription) { this.dialogSubscription.unsubscribe(); }
    if (this.typeChangeSubscription) { this.typeChangeSubscription.unsubscribe(); }
  }
}
